import React, { useState } from 'react';
import { Container, Row, Col, Alert, Button, FormGroup, Label, Input, Card as BSCard, CardHeader } from 'reactstrap';

import C from '../C';
import { assert } from '../base/utils/assert';
import { copyTextToClipboard, encURI, is, modifyHash, stopEvent, yessy } from '../base/utils/miscutils';
import { getId, getName, getStatus, getType } from '../base/data/DataClass';
import DataStore from '../base/plumbing/DataStore';
import ServerIO from '../plumbing/ServerIO';

import { Card } from '../base/components/CardAccordion';
import SubCard from '../base/components/SubCard';
import PropControl from "../base/components/PropControl";
import Misc, { CopyToClipboardButton } from '../base/components/Misc';
import SavePublishDeleteEtc, { confirmUserAction } from '../base/components/SavePublishDeleteEtc';
import RecordVideoWidget from './RecordVideoWidget';

import Advert, { KAdFormat } from '../base/data/Advert';
import { notifyUser } from '../base/plumbing/Messaging';
import {AppNexusId} from './AppNexusWidgets';
import Icon from '../base/components/Icon';

const optPath = (advert) => ['widget', 'preview', advert.id, 'optionParams'];
const getOpts = (advert) => DataStore.getValue(optPath(advert)) || {};

/** Make one or two (if we have PUBLISHED and MODIFIED versions) links to preview the advert 
 * 
 * @param {Object} p
 * @param {String} p.type vast|test|demo|wrapper
*/
function PreviewLink({advert, type, noab, children, params={} }) {
	Advert.assIsa(advert);	
	// draft or published (if not modified)
	const url = makeTestUrl({advert, type, noab, params});
	// If status is MODIFIED (ie published exists AND is different from what's seen) show a link to published also	
	const publishedUrl = getStatus(advert) === C.KStatus.MODIFIED && makeTestUrl({advert, type, noab, params, status:C.KStatus.PUBLISHED});

	return (<div>
		{publishedUrl && 
			<><a href={publishedUrl} target="_new">published{type === 'vast' ? " (contains Xandr/AppNexus macros)" : ""}</a><CopyToClipboardButton text={publishedUrl} /> | </>
		}
		<a href={url} target="_new">
			{children || getStatus(advert).toLowerCase()}
		</a>
		<CopyToClipboardButton text={url} />
	</div>); // NB: wrap in a div to get predictable horizontal flow (ignoring any flex container)
}

/** The link we paste into the DSP (or send to the client) when putting the ad live */
function PublishLink({ advert }) {
	const optionParams = getOpts(advert);
	let content;

	if (KAdFormat.video === advert.format) {
		const vastTagUrl = makeTestUrl({advert, type: 'vast', noab: !optionParams.yesab});

		content = <>
			VAST tag for this advert: <PreviewLink advert={advert} type="vast" /><br />
			<small>
				See it on the{' '}
				{/* Not an error: Google VAST tester needs the "tag" param double-encoded & won't accept it otherwise. */}
				<a href={`https://googleads.github.io/googleads-ima-html5/vsi/?tag=${encodeURIComponent(encodeURIComponent(vastTagUrl))}`} target="_new">
					Google VAST tester
				</a>
				{' '}(tester page is buggy when loading "show this VAST tag" links - if the box above "Test Ad" button is empty, refresh until the URL appears)
			</small>
		</>;
	}

	if (KAdFormat.social === advert.format) {
		content = <>
			Post-engagement page: <PreviewLink advert={advert} type="social" params={{...optionParams}} />
		</>;
	}

	if (KAdFormat.display === advert.format || advert.format==="banner") {
		content= <>
			Brand.com wrapper (possibly broken): <PreviewLink advert={advert} type="wrapper" params={{ ...optionParams }} />
		</>;
	}

	return <SubCard title="Production Link">
		{content}
	</SubCard>;
}


function DemoLink({ advert }) {
	const optionParams = getOpts(advert);

	// We mute splash videos by default so they can always autoplay - enable this to try to play with sound, which may require a click.
	const [unmuteSocial, setUnmuteSocial] = useState(false);
	const unmuteControl = (advert.format === 'social') ? (
		<FormGroup check>
			<Label for="muteSocial">
				<Input type="checkbox" name="muteSocial" onChange={e => setUnmuteSocial(!e.target.checked)} checked={!unmuteSocial} />
				Mute splash video
			</Label>
		</FormGroup>
	) : null;

	return <SubCard title="Demo Link">
		Preview on the public-facing demo page: <PreviewLink advert={advert} type="demo" params={{...optionParams, unmuteSocial }} />
		{unmuteControl}
	</SubCard>;
}

function TestLink({ advert }) {
	const optionParams = getOpts(advert);

	return <SubCard title="Test Link">
		Preview on the internal test page: <PreviewLink advert={advert} type="test" params={{...optionParams }} />
	</SubCard>;
}


function ExportControls({ advert }) {
	const path = optPath(advert);
	const options = DataStore.getValue(path) || {};
	// HACK default to hide portrait and social (requested by Ryan Nov 21)
	if ( ! is(options.hide)) {
		options.hide = ["social", "mobile-portrait"];
		DataStore.setValue(path, options);
	}
	return <>
		<Card title="Links">
			<Container fluid className="mb-2">
				<Row>
					<Col sm="4"><PublishLink advert={advert} /></Col>
					<Col sm="4"><DemoLink advert={advert} /></Col>
					<Col sm="4"><TestLink advert={advert} /></Col>
				</Row>
				<Row>
					<Col sm="6">
						<PropControl className="mr-2" size="sm" type="select" label="Hide these formats on the demo page:" prop="hide" path={path} multiple
							options={["social", "desktop", "mobile-landscape", "mobile-portrait"]}							
						/>
					</Col>
					<Col sm="6">
						<small>These options affect all generated links:</small>
						<div className="flex-row-compact">
							<PropControl className="mr-2" size="sm" type="checkbox" label="Skip to end card" prop="gl.variant" value="tq" path={path} />
							<PropControl className="mr-2" size="sm" type="checkbox" label="Start muted" prop="gl.muted" path={path} />
							{KAdFormat.social === advert.format ? (
								<PropControl className="mr-2" size="sm" type="checkbox" label="No video on social post-engagement page" prop="gl.novideo" path={path} />
							) : null}
							<PropControl className="mr-2" size="sm" type="checkbox" label="Force mobile quality" prop="gl.forceMobile" path={path} />
						</div>
					</Col>
				</Row>
				<small>These settings alter the export links here and now - they aren't saved.</small>
			</Container>

			<div>
				<h5>Impact Hub</h5>
				See <a href={"/#campaign/" + advert.campaign}>this advert's campaign</a> for links.
			</div>
			
			{(KAdFormat.video === advert.format || KAdFormat.banner === advert.format) && <p>
				<a href={'/#tagtest?gl.vert=' + encURI(advert.id)}>HTML tag tool</a>
			</p>}

			<div>
				<h5>AppNexus</h5>
				<div>Current AppNexus ID(s): {advert.aka && advert.aka.length ? advert.aka.map(xid => <AppNexusId key={xid} xid={xid} />) : <code>none</code>}</div>
				<Misc.SubmitButton className="btn btn-default"
					url={`/appnexus/${getType(advert)}/${encURI(advert.id)}`}
					formData={{ action: 'push' }}
					once
				>
					Push this advert to AppNexus
				</Misc.SubmitButton>
			</div>
		</Card>
		
		{KAdFormat.social === advert.format && <RecordVideoWidget isSplash advert={advert} />}

		<RecordVideoWidget advert={advert} />
		
		<Card title="Publish">
			<SavePublishDeleteEtc type={C.TYPES.Advert} id={advert.id}
				className=""
				saveAs={newItem => {
					newItem.budget = {}; // paranoia for old data - no budget (the id would be wrong)
					const nid = getId(newItem);
					modifyHash(['advert', nid]);
					notifyUser({ text: "Switched editor focus to the new copy. Id: " + nid, id: "navchange" });
				}}
				position="relative"
				color="light"
				unpublish
				canArchive
				preSaveAs={confirmUserAction}
				preDelete={confirmUserAction}
				prePublish={() => confirm(
					`Are you sure you want to publish ${getName(advert) || getId(advert)}?
	Publish when the ad is confirmed and ready for the public. Mock-ups should remain draft.`)}
				preArchive={confirmUserAction}
				size="lg"
			/>
		</Card>
	</>;
}


const urlBases = {
	demo: 'demo.good-loop.com/desktop/video/',
	test: 'test.good-loop.com/player/landscape',
	vast: 'as.good-loop.com/vast.xml',
	wrapper: 'as.good-loop.com/pagewrapper.html',
	social: 'social.good-loop.com/generic/', // Not "demo page for social ad" but specifically "the landing page link we put in the DSP when setting up a social ad"
};

// Order of entries determines priority of formats to fall to when selected one is hidden
const demoFormatPaths = {
	desktop: 'desktop/video',
	'mobile-landscape': 'landscape/video',
	'mobile-portrait': 'portrait/video',
	social: 'portrait/social'
};


/**
 * Generate a URL for testing purposes
 * 
 * Hack: Xandr/AppNexus macro REFERER_URL_ENC is added for type=vast && status=published
 * 
 * @param {String} type "demo", test", "vast" or "banner"
 * @param {Object} params Will be converted to URL search params.
 */
const makeTestUrl = ({advert, status, type, noab, params = {}}) => {
	Advert.assIsa(advert);
	const baseParams = {
		'gl.vert': advert.id,
		'gl.status': status || getStatus(advert),
		'gl.noab': !!noab,
		...params
	};
	params = {...baseParams, ...params};

	let hide = params.hide;
	if ( ! Array.isArray(hide) && hide && hide.split) hide = hide.split(",");
	// Override: Social ads should only be demo'd as social, so hide other formats.
	if (KAdFormat.social === advert.format) {
		hide = ['desktop', 'mobile-landscape', 'mobile-portrait'];
	}

	// required for demo.gl, pass over what type of ad this is (video, social, display...)
	params['gl.format'] = advert.format;

	let domainPrefix = C.SERVER_TYPE;
	// Allow viewing prod adverts on the test page - instruct it to fetch unit.js and unit.json from as.good-loop.com
	if (type === 'test' && domainPrefix === '') {
		params.codeServer = 'prod';
		params.dataServer = 'prod';
	}
	// Test pages break the domain pattern by being "localtest", "test", (no prod equivalent)
	if (type === 'test' && domainPrefix === 'test') domainPrefix = '';

	// Set production-ready params on published VAST tag
	if (type === 'vast' && params['gl.status'] === C.KStatus.PUBLISHED) {
		params['gl.site'] = 'REFERER_URL_ENC'; // This is supposed to be surrounded with ${} - but we add that last, so it doesn't get autoescaped by URL()
		params['gl.noab'] = false;
		params['gl.delivery'] = 'vpaid';
	}

	// Generate URL object...
	let base = `${C.HTTPS}://${domainPrefix}${urlBases[type]}`;

	// Set default format to view for the demo page
	// Copy all paths to remember which are unusable
	let potentialPaths = { ...demoFormatPaths };
	if (hide) {
		// Cycle through each hidden format and check if the link uses it
		hide.forEach(format => {
			// remove the path from the potential list
			delete potentialPaths[format];
			if (base.includes(demoFormatPaths[format])) {
				// If it does, go to the next first path available
				base = base.replace(demoFormatPaths[format], Object.values(potentialPaths)[0]);
			}
		});
	}

	const url = new URL(base);

	// Add all requested params
	Object.entries(params).forEach(([key, val]) => {
		if (key && val) url.searchParams.append(key, val);
	});

	// Render to string
	// eslint-disable-next-line no-template-curly-in-string
	return url.toString().replace('REFERER_URL_ENC', '${REFERER_URL_ENC}'); // Not a mistake, AppNexus macros use ${} as delimiter
};


export { PreviewLink };
export default ExportControls;
